import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { authApi, careEntriesApi, importApi, measurementsApi, settingsApi, testDataApi, UnauthorizedError, getToken } from './api/client';
import { AGENTS, DEFAULT_SETTINGS } from './domain/constants';
import { doses, doseStepFor } from './domain/doses';
import { latestValue } from './domain/measurements';
import { noteEntryFor } from './domain/care';
import { parseCsv, toCsv } from './domain/csv';
import { dateText, dayOf, parse, today } from './domain/format';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { MeasurementSheet } from './components/MeasurementSheet';
import { ChemicalSheet } from './components/ChemicalSheet';
import { NoteSheet } from './components/NoteSheet';
import { CareLogPage } from './components/CareLogPage';
import { SettingsPage } from './components/SettingsPage';
import { Toast } from './components/Toast';
export function App() {
    const [authed, setAuthed] = useState(() => getToken() !== null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [measurements, setMeasurements] = useState([]);
    const [careEntries, setCareEntries] = useState([]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [page, setPage] = useState(0);
    const [toast, setToast] = useState(null);
    const toastTimeout = useRef(null);
    const [sessionNotice, setSessionNotice] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [doseSheet, setDoseSheet] = useState(null);
    const [noteSheet, setNoteSheet] = useState(null);
    const [logOpen, setLogOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const flash = (msg, kind = 'info') => {
        setToast({ message: msg, kind });
        if (toastTimeout.current)
            window.clearTimeout(toastTimeout.current);
        toastTimeout.current = window.setTimeout(() => setToast(null), 2600);
    };
    useEffect(() => {
        if (!authed)
            return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        Promise.all([measurementsApi.list(), careEntriesApi.list(), settingsApi.get()])
            .then(([m, c, s]) => {
            if (cancelled)
                return;
            setMeasurements(m);
            setCareEntries(c);
            setSettings(s);
        })
            .catch(err => {
            if (cancelled)
                return;
            if (err instanceof UnauthorizedError)
                setAuthed(false);
            else
                setLoadError(err instanceof Error ? err.message : 'Fehler beim Laden.');
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [authed]);
    useEffect(() => {
        const anyOpen = !authed || sheet !== null || doseSheet !== null || noteSheet !== null || logOpen || settingsOpen;
        if (!anyOpen)
            return;
        const body = document.body;
        const scrollY = window.scrollY;
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.overflow = 'hidden';
        return () => {
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.overflow = '';
            window.scrollTo(0, scrollY);
        };
    }, [authed, sheet, doseSheet, noteSheet, logOpen, settingsOpen]);
    const handleLogin = async (username, password) => {
        await authApi.login(username, password);
        setSessionNotice(null);
        setAuthed(true);
    };
    const logout = () => {
        authApi.logout();
        setAuthed(false);
        setSheet(null);
        setDoseSheet(null);
        setNoteSheet(null);
        setLogOpen(false);
        setSettingsOpen(false);
        setMeasurements([]);
        setCareEntries([]);
        setSettings(DEFAULT_SETTINGS);
    };
    const handleMutationError = (err) => {
        if (err instanceof UnauthorizedError) {
            logout();
            setSessionNotice('Sitzung abgelaufen, bitte erneut anmelden.');
        }
        else if (err instanceof TypeError) {
            // fetch wirft TypeError bei Netzwerkfehlern ("Failed to fetch") – englische Message nicht durchreichen
            flash('Netzwerkfehler – Änderung wurde nicht gespeichert.', 'error');
        }
        else {
            flash(err instanceof Error && err.message ? err.message : 'Aktion fehlgeschlagen.', 'error');
        }
    };
    const runMutation = (action) => {
        void action().catch(handleMutationError);
    };
    const syncNote = async (date, text) => {
        const trimmed = text.trim();
        const existing = noteEntryFor(careEntries, date);
        if (existing && !trimmed) {
            await careEntriesApi.remove(existing.id);
            setCareEntries(cs => cs.filter(c => c.id !== existing.id));
        }
        else if (existing) {
            const updated = await careEntriesApi.update(existing.id, { type: 'note', date, agent: null, grams: null, note: trimmed });
            setCareEntries(cs => cs.map(c => (c.id === existing.id ? updated : c)));
        }
        else if (trimmed) {
            const created = await careEntriesApi.create({ type: 'note', date, agent: null, grams: null, note: trimmed });
            setCareEntries(cs => [...cs, created]);
        }
    };
    const defaultDose = (lph, lcl) => {
        const sug = doses(lph?.ph ?? null, lcl?.cl ?? null, settings)[0];
        return {
            doseAgent: sug ? sug.agentKey : 'phMinus',
            doseGrams: sug ? sug.grams : doseStepFor('phMinus', settings)
        };
    };
    const openSheetFor = (which) => {
        const t = today();
        const lph = latestValue(measurements, 'ph');
        const lcl = latestValue(measurements, 'cl');
        const lta = latestValue(measurements, 'ta');
        const existingNote = noteEntryFor(careEntries, t);
        const flags = which === 'new'
            ? { ph: true, cl: true, ta: false }
            : which === 'ph'
                ? { ph: true, cl: false, ta: false }
                : which === 'cl'
                    ? { ph: false, cl: true, ta: false }
                    : { ph: false, cl: false, ta: true };
        setSheet({
            editId: null,
            careIds: { filter: null, robot: null, dose: null },
            draft: {
                date: t,
                phOn: flags.ph,
                clOn: flags.cl,
                taOn: flags.ta,
                ph: lph?.ph ?? 7.2,
                cl: lcl?.cl ?? 0.45,
                ta: lta?.ta ?? 100,
                note: existingNote?.note ?? '',
                filterOn: false,
                robotOn: false,
                doseOn: false,
                ...defaultDose(lph, lcl)
            }
        });
    };
    const editMeasurement = (m) => {
        const existingNote = noteEntryFor(careEntries, m.date);
        const filterEntry = careEntries.find(c => c.type === 'filter' && c.date === m.date) ?? null;
        const robotEntry = careEntries.find(c => c.type === 'robot' && c.date === m.date) ?? null;
        const doseEntry = careEntries.find(c => c.type === 'dose' && c.date === m.date) ?? null;
        const lph = latestValue(measurements, 'ph');
        const lcl = latestValue(measurements, 'cl');
        const fallbackDose = defaultDose(lph, lcl);
        setSheet({
            editId: m.id,
            careIds: { filter: filterEntry?.id ?? null, robot: robotEntry?.id ?? null, dose: doseEntry?.id ?? null },
            draft: {
                date: m.date,
                phOn: m.ph !== null,
                clOn: m.cl !== null,
                taOn: m.ta !== null,
                ph: m.ph ?? 7.2,
                cl: m.cl ?? 0.45,
                ta: m.ta ?? 100,
                note: existingNote?.note ?? '',
                filterOn: filterEntry !== null,
                robotOn: robotEntry !== null,
                doseOn: doseEntry !== null,
                doseAgent: doseEntry?.agent ?? fallbackDose.doseAgent,
                doseGrams: doseEntry?.grams ?? fallbackDose.doseGrams
            }
        });
    };
    const careExtraLabel = (entry) => entry.type === 'filter' ? 'Filterwechsel' : entry.type === 'robot' ? 'Robi-Lauf' : entry.agent ? `${entry.grams} g ${AGENTS[entry.agent].label}` : 'Chemikalie';
    // Gleicht die Pflege-Toggles des Sheets mit den Einträgen ab: beim Bearbeiten werden die
    // vorbefüllten Einträge (careIds) aktualisiert bzw. bei ausgeschaltetem Toggle gelöscht,
    // sonst werden neue Einträge angelegt. Liefert die Labels der neu angelegten Einträge.
    const syncCareExtras = async (draft, ids) => {
        const created = [];
        const updatedEntries = [];
        const removedIds = [];
        const byId = (id) => (id === null ? null : (careEntries.find(c => c.id === id) ?? null));
        const syncToggle = async (type, on, id) => {
            const existing = byId(id);
            if (on && existing) {
                if (existing.date !== draft.date) {
                    updatedEntries.push(await careEntriesApi.update(existing.id, { type, date: draft.date, agent: null, grams: null, note: existing.note }));
                }
            }
            else if (on && !careEntries.some(c => c.type === type && c.date === draft.date)) {
                created.push(await careEntriesApi.create({ type, date: draft.date, agent: null, grams: null, note: null }));
            }
            else if (!on && existing) {
                await careEntriesApi.remove(existing.id);
                removedIds.push(existing.id);
            }
        };
        await syncToggle('filter', draft.filterOn, ids.filter);
        await syncToggle('robot', draft.robotOn, ids.robot);
        const doseEntry = byId(ids.dose);
        if (draft.doseOn && doseEntry) {
            if (doseEntry.date !== draft.date || doseEntry.agent !== draft.doseAgent || doseEntry.grams !== draft.doseGrams) {
                updatedEntries.push(await careEntriesApi.update(doseEntry.id, { type: 'dose', date: draft.date, agent: draft.doseAgent, grams: draft.doseGrams, note: doseEntry.note }));
            }
        }
        else if (draft.doseOn) {
            created.push(await careEntriesApi.create({ type: 'dose', date: draft.date, agent: draft.doseAgent, grams: draft.doseGrams, note: null }));
        }
        else if (doseEntry) {
            await careEntriesApi.remove(doseEntry.id);
            removedIds.push(doseEntry.id);
        }
        if (created.length || updatedEntries.length || removedIds.length) {
            setCareEntries(cs => [
                ...cs.filter(c => !removedIds.includes(c.id)).map(c => updatedEntries.find(u => u.id === c.id) ?? c),
                ...created
            ]);
        }
        return created.map(careExtraLabel);
    };
    const saveMeasurement = async (draft) => {
        if (!sheet)
            return;
        await syncNote(draft.date, draft.note);
        const rec = { date: draft.date, ph: draft.phOn ? draft.ph : null, cl: draft.clOn ? draft.cl : null, ta: draft.taOn ? draft.ta : null };
        let message;
        if (sheet.editId !== null) {
            const updated = await measurementsApi.update(sheet.editId, rec);
            setMeasurements(ms => ms.map(m => (m.id === sheet.editId ? updated : m)));
            message = 'Änderungen gespeichert';
        }
        else {
            const same = measurements.find(m => m.date === draft.date);
            if (same) {
                const merged = {
                    date: draft.date,
                    ph: draft.phOn ? draft.ph : same.ph,
                    cl: draft.clOn ? draft.cl : same.cl,
                    ta: draft.taOn ? draft.ta : same.ta
                };
                const updated = await measurementsApi.update(same.id, merged);
                setMeasurements(ms => ms.map(m => (m.id === same.id ? updated : m)));
                message = 'Messung aktualisiert';
            }
            else {
                const created = await measurementsApi.create(rec);
                setMeasurements(ms => [...ms, created]);
                message = 'Messung gespeichert';
            }
        }
        const extraLabels = await syncCareExtras(draft, sheet.careIds);
        if (extraLabels.length)
            message += ` (+ ${extraLabels.join(', ')})`;
        flash(message);
        setPage(0);
        setSheet(null);
    };
    const deleteMeasurement = async () => {
        if (!sheet || sheet.editId === null)
            return;
        const id = sheet.editId;
        await measurementsApi.remove(id);
        setMeasurements(ms => ms.filter(m => m.id !== id));
        setSheet(null);
        flash('Messung gelöscht');
    };
    const toggleCare = async (type) => {
        const t = today();
        const existing = careEntries.find(c => c.type === type && c.date === t);
        const label = type === 'filter' ? 'Filterwechsel' : 'Robi-Lauf';
        if (existing) {
            await careEntriesApi.remove(existing.id);
            setCareEntries(cs => cs.filter(c => c.id !== existing.id));
            flash(`${label} für heute entfernt`);
        }
        else {
            const created = await careEntriesApi.create({ type, date: t, agent: null, grams: null, note: null });
            setCareEntries(cs => [...cs, created]);
            flash(`${label} für heute eingetragen`);
        }
    };
    const logDoseDirect = async (agentKey, grams) => {
        const created = await careEntriesApi.create({ type: 'dose', date: today(), agent: agentKey, grams, note: null });
        setCareEntries(cs => [...cs, created]);
        flash(`${grams} g ${AGENTS[agentKey].label} dokumentiert`);
    };
    const openDose = () => {
        const lph = latestValue(measurements, 'ph');
        const lcl = latestValue(measurements, 'cl');
        const sug = doses(lph?.ph ?? null, lcl?.cl ?? null, settings)[0];
        setDoseSheet({
            agent: sug ? sug.agentKey : 'phMinus',
            grams: sug ? sug.grams : doseStepFor('phMinus', settings),
            date: today()
        });
    };
    const saveDose = async (draft) => {
        const created = await careEntriesApi.create({ type: 'dose', date: draft.date, agent: draft.agent, grams: draft.grams, note: null });
        setCareEntries(cs => [...cs, created]);
        setDoseSheet(null);
        flash(`${draft.grams} g ${AGENTS[draft.agent].label} dokumentiert`);
    };
    const openDayNote = () => {
        const t = today();
        const ex = noteEntryFor(careEntries, t);
        setNoteSheet({
            mode: 'day',
            entryId: null,
            title: 'Notiz für heute',
            subtitle: `${dayOf(t)}, ${dateText(t)}${parse(t).getFullYear()}`,
            text: ex?.note ?? ''
        });
    };
    const editEntryNote = (entry) => {
        setNoteSheet({
            mode: 'entry',
            entryId: entry.id,
            title: 'Notiz zum Eintrag',
            subtitle: 'Erscheint beim Eintrag im Pflege-Protokoll.',
            text: entry.note ?? ''
        });
    };
    const saveNote = async (text) => {
        if (!noteSheet)
            return;
        const trimmed = text.trim();
        if (noteSheet.mode === 'day') {
            await syncNote(today(), trimmed);
        }
        else if (noteSheet.entryId !== null) {
            const entry = careEntries.find(c => c.id === noteSheet.entryId);
            if (entry) {
                const updated = await careEntriesApi.update(entry.id, { type: entry.type, date: entry.date, agent: entry.agent, grams: entry.grams, note: trimmed || null });
                setCareEntries(cs => cs.map(c => (c.id === entry.id ? updated : c)));
            }
        }
        setNoteSheet(null);
        flash(trimmed ? 'Notiz gespeichert' : 'Notiz entfernt');
    };
    const removeCareEntry = async (id) => {
        await careEntriesApi.remove(id);
        setCareEntries(cs => cs.filter(c => c.id !== id));
    };
    const changePassword = async (currentPassword, newPassword) => {
        try {
            await authApi.changePassword(currentPassword, newPassword);
        }
        catch (err) {
            if (err instanceof UnauthorizedError) {
                handleMutationError(err);
                return;
            }
            // Andere Fehler zeigt die ChangePasswordCard inline an
            throw err;
        }
        flash('Passwort geändert.');
    };
    const seedTestData = async () => {
        const seeded = await testDataApi.seed();
        setMeasurements(seeded.measurements);
        setCareEntries(seeded.careEntries);
        setPage(0);
        flash('Testdaten erzeugt');
    };
    const clearAllData = async () => {
        await testDataApi.clear();
        setMeasurements([]);
        setCareEntries([]);
        setPage(0);
        flash('Alle Daten gelöscht');
    };
    const exportCsv = () => {
        const blob = new Blob([toCsv(measurements, careEntries)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `poolmanager-export-${today()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        flash('CSV exportiert');
    };
    const importCsv = (text) => {
        const result = parseCsv(text);
        if (!result.ok) {
            flash(result.error, 'error');
            return;
        }
        // Confirm erst nach dem Parsen, damit die Meldung die Anzahlen nennen kann
        const msg = `${result.measurements.length} Messungen und ${result.careEntries.length} Pflege-Einträge importieren? Alle vorhandenen Daten werden ersetzt.`;
        if (!window.confirm(msg))
            return;
        runMutation(async () => {
            const imported = await importApi.replaceAll({ measurements: result.measurements, careEntries: result.careEntries });
            setMeasurements(imported.measurements);
            setCareEntries(imported.careEntries);
            setPage(0);
            flash('CSV importiert');
        });
    };
    const changeSettings = async (next) => {
        setSettings(next);
        try {
            const saved = await settingsApi.update(next);
            setSettings(saved);
        }
        catch (err) {
            handleMutationError(err);
        }
    };
    if (!authed) {
        return _jsx(Login, { onLogin: handleLogin, notice: sessionNotice });
    }
    if (loading && measurements.length === 0 && !loadError) {
        return _jsx("div", { style: { padding: 40, font: '600 14px var(--font-body)', color: 'var(--text-secondary)' }, children: "L\u00E4dt \u2026" });
    }
    if (loadError) {
        return _jsx("div", { style: { padding: 40, font: '600 14px var(--font-body)', color: 'var(--status-high-fg)' }, children: loadError });
    }
    const lph = latestValue(measurements, 'ph');
    const lcl = latestValue(measurements, 'cl');
    return (_jsxs(_Fragment, { children: [_jsx(Dashboard, { measurements: measurements, careEntries: careEntries, settings: settings, page: page, onPageChange: setPage, onOpenSheetFor: openSheetFor, onEditMeasurement: editMeasurement, onOpenSettings: () => setSettingsOpen(true), onOpenLog: () => setLogOpen(true), onOpenDose: openDose, onOpenDayNote: openDayNote, onToggleCare: type => runMutation(() => toggleCare(type)), onLogDose: (agentKey, grams) => runMutation(() => logDoseDirect(agentKey, grams)), onLogout: logout }), logOpen && (_jsx(CareLogPage, { careEntries: careEntries, onBack: () => setLogOpen(false), onEditNote: editEntryNote, onRemove: id => runMutation(() => removeCareEntry(id)) })), settingsOpen && (_jsx(SettingsPage, { settings: settings, onChange: next => void changeSettings(next), onReset: () => void changeSettings(DEFAULT_SETTINGS), onBack: () => setSettingsOpen(false), onChangePassword: changePassword, onSeedTestData: () => runMutation(seedTestData), onClearData: () => runMutation(clearAllData), onExportCsv: exportCsv, onImportCsv: importCsv })), sheet && (_jsx(MeasurementSheet, { isEdit: sheet.editId !== null, initial: sheet.draft, careEntries: careEntries, settings: settings, phV: lph?.ph ?? null, clV: lcl?.cl ?? null, onClose: () => setSheet(null), onSave: draft => runMutation(() => saveMeasurement(draft)), onDelete: () => runMutation(() => deleteMeasurement()) })), doseSheet && (_jsx(ChemicalSheet, { initial: doseSheet, settings: settings, phV: lph?.ph ?? null, clV: lcl?.cl ?? null, onClose: () => setDoseSheet(null), onSave: draft => runMutation(() => saveDose(draft)) })), noteSheet && (_jsx(NoteSheet, { title: noteSheet.title, subtitle: noteSheet.subtitle, initialText: noteSheet.text, onClose: () => setNoteSheet(null), onSave: text => runMutation(() => saveNote(text)) })), _jsx(Toast, { message: toast?.message ?? null, kind: toast?.kind ?? 'info' })] }));
}
